import { useMediaQuery } from '@mui/material';
import React, { memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

import CommonLoader from '../components/common-loader/CommonLoader';
import CommonPage from '../components/common-page/CommonPage.jsx';
import { Col, Row } from '../components/layout/layout';
import { SONAR_RANGE_TYPE, SORT_ORDER } from '../constants/index.js';
import { getSonarData, getSonarNavig8 } from '../redux/sonar/sonarActions';
import { cancelNavig8, resetSonarData, selectSonarTab } from '../redux/sonar/sonarReducer';
import { SONAR_DATA_KEYS } from '../utils/chart.js';
import timeout from '../utils/timeout.js';
import Brand from './brand/Brand.jsx';
import Dashboard from './dashboard/Dashboard.jsx';
import DedicatedTableColumn from './dedicated-table/DedicatedTableColumn.jsx';
import Engagement from './engagement/Engagement.jsx';
import EngagementAll from './engagement/EngagementAll.jsx';
import Interest from './interest/Interest.jsx';
import InterestAll from './interest/InterestAll.jsx';
import Navig8NotificationBarController from './navig8/components/navig8-notification-bar-controller/Navig8NotificationBarController.jsx';
import Navig8 from './navig8/Navig8.jsx';
import Powerrank from './powerrank/Powerrank.jsx';
import Presence from './presence/Presence.jsx';
import PresenceAll from './presence/PresenceAll.jsx';
import Relevance from './relevance/Relevance.jsx';
import RelevanceAll from './relevance/RelevanceAll.jsx';
import Rivalry from './rivalry/Rivalry.jsx';
import RivalryAll from './rivalry/RivalryAll.jsx';
import SonarTable from './sonar-table/SonarTable.jsx';
import Trust from './trust/Trust.jsx';
import TrustAll from './trust/TrustAll.jsx';

import './Dashboard.scss';

const SonarDashboard = () => {
  const { t } = useTranslation('sonar');
  const { id } = useParams();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 600px)');

  const {
    [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
    [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
  } = useSelector((state) => state.sonarDashboard.dateRanges);

  const selectedTab = useSelector((state) => state.sonarDashboard.selectedTab);
  const isLoading = useSelector((state) => state.sonarDashboard.isLoading);
  const navig8 = useSelector((state) => state.sonarDashboard.navig8);
  const navig8AbortController = useSelector((state) => state.sonarDashboard.navig8AbortController);
  const isShowDedicated = useSelector((state) => state.sonarDashboard.isShowDedicated);

  const chartsWidth = isShowDedicated ? 75 : 100;

  const handleChangeTab = (newTab) => {
    dispatch(selectSonarTab(newTab));
  };
  const handleReinitializeNavig8 = async () => {
    dispatch(cancelNavig8());
    await timeout(100);
    dispatch(getSonarNavig8(id));
  };

  useEffect(() => {
    if (!id) {
      return;
    }

    dispatch(getSonarData({ id }));
  }, [comparableEndDate, comparableStartDate, compareToEndDate, compareToStartDate, id, dispatch]);

  useEffect(() => {
    if (navig8.thread || (!navig8.thread && navig8AbortController)) {
      handleReinitializeNavig8();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comparableEndDate, comparableStartDate, compareToEndDate, compareToStartDate, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(cancelNavig8());
      dispatch(resetSonarData());
    };
  }, [dispatch]);

  return (
    <CommonPage className='sonar-dashboard'>
      <Tabs selectedIndex={selectedTab} onSelect={handleChangeTab}>
        <div className='tab-list_wrapper'>
          <TabList>
            <Tab>{t('dashboard')}</Tab>
            <Tab disabled={isLoading}>NAVIG8</Tab>
            <Tab disabled={isLoading}>{t('powerrank')}</Tab>
            <Tab disabled={isLoading}>{t('data')}</Tab>
            <Tab disabled={isLoading}>{t('interest')}</Tab>
            <Tab disabled={isLoading}>{t('relevance')}</Tab>
            <Tab disabled={isLoading}>{t('trust')}</Tab>
            <Tab disabled={isLoading}>{t('engagement')}</Tab>
            <Tab disabled={isLoading}>{t('rivalry')}</Tab>
            <Tab disabled={isLoading}>{t('presence')}</Tab>
            <Tab disabled={isLoading}>{t('brand')}</Tab>
          </TabList>
        </div>

        <TabPanel>
          <Dashboard />
        </TabPanel>

        <TabPanel>
          <Navig8 />
        </TabPanel>

        <TabPanel>
          <Powerrank />
        </TabPanel>

        <TabPanel>
          <SonarTable />
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Interest />
              {!isMobile ? <InterestAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['interest']}
              initialSort={{
                key: 'interest',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('interest')}`}
              rankKey={SONAR_DATA_KEYS.INTEREST}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Relevance />
              {!isMobile ? <RelevanceAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['relevanceStrength', 'relevanceBreadth']}
              initialSort={{
                key: 'relevanceStrength',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('relevanceStrength')}`}
              rankKey={SONAR_DATA_KEYS.RELEVANCE_STRENGTH}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Trust />
              {!isMobile ? <TrustAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['trust']}
              initialSort={{
                key: 'trust',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('trust')}`}
              rankKey={SONAR_DATA_KEYS.TRUST}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Engagement />
              {!isMobile ? <EngagementAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['passion']}
              initialSort={{
                key: 'passion',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('engagement')}`}
              rankKey={SONAR_DATA_KEYS.PASSION}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Rivalry />
              {!isMobile ? <RivalryAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['friction']}
              initialSort={{
                key: 'friction',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('rivalry')}`}
              rankKey={SONAR_DATA_KEYS.FRICTION}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Presence />
              {!isMobile ? <PresenceAll /> : null}
            </Col>

            <DedicatedTableColumn
              columnsToShow={['presence', 'energy']}
              initialSort={{
                key: 'presence',
                order: SORT_ORDER.DESC,
              }}
              rankLabel={`${t('rank')} ${t('reach')}`}
              rankKey={SONAR_DATA_KEYS.PRESENCE}
            />
          </Row>
        </TabPanel>

        <TabPanel>
          <Row className='sonar-dashboard_row'>
            <Col width={chartsWidth} className='sonar-dashboard_content'>
              <Brand />
            </Col>

            <DedicatedTableColumn columnsToShow={['powerrank']} rankKey={SONAR_DATA_KEYS.POWERRANK} disableGlobalRank />
          </Row>
        </TabPanel>
      </Tabs>

      <CommonLoader isLoading={isLoading} />
      <Navig8NotificationBarController />
    </CommonPage>
  );
};

export default memo(SonarDashboard);
